import { useCallback, useEffect, useRef, useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { reportAdLauncherError } from "@/lib/ad-launcher-error";

export type DetectedField<T = string> = {
  value: T | null;
  status: "detected" | "majority" | "ambiguous" | "none";
  note?: string;
  candidates?: Array<{ value: string; share: number }>;
};

export type DetectionResponse = {
  connectedAccountId: string;
  accountName: string | null;
  windowDays: number;
  detection: {
    pageId: DetectedField;
    pixelId: DetectedField;
    vertical: DetectedField;
    dailyBudgetCapMinor: DetectedField<number>;
    naming: DetectedField<{ brand: string; product_line?: string }>;
    leadSource?: DetectedField;
  };
};

type DetectionState = {
  result: DetectionResponse | null;
  status: "idle" | "loading" | "success" | "error";
};

export function useLaunchAccountDetection(
  brandId: string,
  selectedAccountId: string,
  active: boolean,
  onResult: (result: DetectionResponse) => void,
) {
  const generation = useRef(0);
  const mounted = useRef(true);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  const [state, setState] = useState<DetectionState>({ result: null, status: "idle" });

  const run = useCallback(async (fresh: boolean, expectedId: string) => {
    if (!expectedId || !/^\d+$/.test(expectedId)) return;
    const requestGeneration = ++generation.current;
    setState((current) => ({ ...current, status: "loading" }));
    try {
      const params = new URLSearchParams({ availableAccountId: expectedId });
      if (fresh) params.set("fresh", "1");
      const response = await apiRequest(
        "GET",
        `/api/windsor/brands/${brandId}/detect-config?${params.toString()}`,
      );
      const result = (await response.json()) as DetectionResponse;
      if (!mounted.current || generation.current !== requestGeneration || !active || selectedAccountId !== expectedId) return;
      if (String(result.connectedAccountId) !== expectedId) {
        setState({ result: null, status: "error" });
        return;
      }
      setState({ result, status: "success" });
      onResultRef.current(result);
    } catch (error) {
      if (!mounted.current || generation.current !== requestGeneration || !active || selectedAccountId !== expectedId) return;
      reportAdLauncherError(
        "launch-account-detect-config",
        error instanceof Error ? error : new Error(String(error)),
      );
      setState({ result: null, status: "error" });
    }
  }, [active, brandId, selectedAccountId]);

  useEffect(() => {
    mounted.current = true;
    const currentGeneration = ++generation.current;
    setState({ result: null, status: "idle" });
    if (active && selectedAccountId) {
      void run(false, selectedAccountId);
    }
    return () => {
      generation.current = Math.max(generation.current, currentGeneration) + 1;
    };
  }, [active, selectedAccountId, run]);

  useEffect(() => () => {
    mounted.current = false;
    generation.current += 1;
  }, []);

  const retry = useCallback(() => {
    if (active && selectedAccountId) void run(true, selectedAccountId);
  }, [active, selectedAccountId, run]);

  return { ...state, retry };
}
